import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useRef, useEffect, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import { AddIcon, AssessmentIcon, EditIcon, FileOpenIcon, PrintIcon, SaveIcon, SettingsIcon } from "../../icons";
import { ReportPrintList, ReportSettingList } from "../MouseClickList";
import { t } from "i18next";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { isCreatingReportState, selectedReportListState } from "../../recoil";
import { ReportItem, TemplateReportItem } from "../../@types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DragIndicatorIcon } from "../../icons";
import TemplateModal from "./TemplateModal";
import { ContainedButtonStyles } from "../../styles/GlobalStyles";
import { getReportData, saveReportConfig } from "../../services/reportService";
import DateRange from "./DateRange";
import { NewReport } from "./NewReport";
import { toast } from "react-toastify";
import { handleSaveFilter } from "../../utils/utils";
import ReportCard from "./ReportCard";
import { EDITED_TEXT_FILTER, REPORT_CONTENTS, ValidationInfo } from "./ReportContents";
import { ReportRawData, buildReportMetrics } from "./reportMetrics";
import { useColorTheme } from "./setting/ColorThemeContext";

export default function DetailContent({
  config,
  setConfig,
  type,
}: {
  config: reportConfigItem | null;
  setConfig: (config: reportConfigItem | null) => void;
  type: string;
}) {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [anchorElAdd, setAnchorElAdd] = useState<null | HTMLElement>(null);
  const [anchorElPrint, setAnchorElPrint] = useState<null | HTMLElement>(null);
  const [anchorElSetting, setAnchorElSetting] = useState<null | HTMLElement>(null);
  const [selectedReportList, setSelectedReportList] = useRecoilState(selectedReportListState);
  const addOpen = Boolean(anchorElAdd);
  const printOpen = Boolean(anchorElPrint);
  const settingOpen = Boolean(anchorElSetting);
  const [rawData, setRawData] = useState<ReportRawData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentColors } = useColorTheme();
  const [editingId, setEditingId] = useState<string | null>(null); // 편집 중인 콘텐츠(분석 소견)
  const [validHistoryId, setValidHistoryId] = useState<number | undefined>(config?.valid_history_id ?? undefined);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<{
    year: number;
    quarter?: number;
  } | null>(null); // 모달에서 선택한 기간
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [reportName, setReportName] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);
  const isCreatingReport = useRecoilValue(isCreatingReportState);
  const [initialState, setInitialState] = useState<reportConfigItem | null>({
    name: "",
    date_range: { start_date: "", end_date: "" },
    color_theme: "",
    class_filter: null,
    contents: "",
  });

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    if (config?.valid_history_id) setValidHistoryId(config.valid_history_id);
  }, [config?.valid_history_id]);

  useEffect(() => {
    setIsLoading(true);
    getReportData(Number(id), validHistoryId)
      .then(setRawData)
      .catch((error) => toast.error(error.message))
      .finally(() => setIsLoading(false));
  }, [id, validHistoryId]);

  const metrics = useMemo(
    () =>
      rawData &&
      buildReportMetrics(rawData, {
        dateRange,
        classFilter: config?.class_filter,
      }),
    [rawData, dateRange, config?.class_filter],
  );

  useEffect(() => {
    if (config) {
      setInitialState(config);
      setReportName(config?.name || "Report");
      setDateRange({
        start_date: config?.date_range?.start_date || "",
        end_date: config?.date_range?.end_date || "",
      });
    }
  }, [config]);

  const templateItems = selectedReportList.map(({ gridSize, type, filter }, idx) => {
    const templateItem: TemplateReportItem = {
      index: idx + 1,
      gridSize,
      type,
      filter,
    };

    if (filter) {
      templateItem.filter = filter;
    }

    return templateItem;
  });

  useEffect(() => {
    const hasNameChanged = reportName !== initialState?.name;
    const hasListChanged = JSON.stringify(templateItems) !== config?.contents;
    const hasRangeChanged = JSON.stringify(dateRange) !== JSON.stringify(config?.date_range ?? dateRange);
    const hasValidChanged = validHistoryId !== undefined && validHistoryId !== config?.valid_history_id;
    setHasChanges(hasNameChanged || hasListChanged || hasRangeChanged || hasValidChanged);
  }, [initialState, reportName, selectedReportList, dateRange, validHistoryId]);

  const handleAddContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorElAdd(event.currentTarget);
    setAnchorElPrint(null);
    setAnchorElSetting(null);
  };

  const handlePrintContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorElPrint(event.currentTarget);
    setAnchorElAdd(null);
    setAnchorElSetting(null);
  };

  const handleSettingContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorElSetting(event.currentTarget);
    setAnchorElAdd(null);
    setAnchorElPrint(null);
  };

  const handleClose = () => {
    setAnchorElAdd(null);
    setAnchorElPrint(null);
    setAnchorElSetting(null);
  };

  const handleSave = async () => {
    try {
      const response = await saveReportConfig({
        id: Number(id),
        name: reportName,
        date_range: dateRange,
        contents: JSON.stringify(templateItems),
        ...(rawData?.validation && { valid_history_id: rawData.validation.id }),
      });
      setHasChanges(false);
      toast.success(response.message);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportPDF = useReactToPrint({
    contentRef,
    documentTitle: "DetailContent-Export",
    pageStyle: `
      body {
        padding: 12px;
      }
    `,
  });

  // 메뉴 아이템 클릭
  const handleMenuItemClick = (contentType: string) => {
    const newItem: ReportItem = {
      index: selectedReportList.length,
      id: `${contentType}-${Math.floor(100000 + Math.random() * 900000)}`,
      type: contentType,
      gridSize: REPORT_CONTENTS[contentType].gridSize,
    };
    setSelectedReportList((prev) => [...prev, newItem]);
    handleClose();
  };

  // 그리드 사이즈 변경
  const handleGridSizeChange = (id: string, size: 4 | 6 | 8 | 12) => {
    setSelectedReportList((prev) => prev.map((item) => (item.id === id ? { ...item, gridSize: size } : item)));
  };

  // 드래그 앤 드랍
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(selectedReportList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // 인덱스 업데이트
    const updatedItems = items.map((item, index) => ({
      ...item,
      index,
    }));

    setSelectedReportList(updatedItems);
  };

  const renderContent = () => {
    return (
      <Box>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reports" direction="horizontal">
            {(provided: any) => (
              <Grid container spacing={3} {...provided.droppableProps} ref={provided.innerRef}>
                {selectedReportList.map((contentType, index) => {
                  const content = REPORT_CONTENTS[contentType.type];

                  return (
                    <Draggable key={contentType.id} draggableId={contentType.id} index={index}>
                      {(provided, snapshot) => (
                        <Grid
                          item
                          xs={contentType.gridSize}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          onMouseEnter={() => setHoveredItemId(contentType.id)}
                          onMouseLeave={() => setHoveredItemId(null)}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              position: "relative",
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              p: 2,
                              backgroundColor: snapshot.isDragging ? "rgba(0, 0, 0, 0.05)" : "#fff",
                              boxShadow: snapshot.isDragging
                                ? "0 8px 16px rgba(0,0,0,0.1)"
                                : "0 2px 4px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.05)",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.05)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                marginRight: 1,
                                marginBottom: 1,
                                opacity: hoveredItemId === contentType.id ? 1 : 0,
                                transition: "opacity 0.2s",
                                "&:active": { cursor: "grabbing" },
                              }}
                            >
                              <DragIndicatorIcon sx={{ color: "var(--icon-color)" }} />
                            </Box>

                            {content && metrics && (
                              <ReportCard
                                contentId={contentType.id}
                                title={content.title}
                                onGridSizeChange={handleGridSizeChange}
                                actions={
                                  content.editable && (
                                    <Tooltip title={t("Edit")}>
                                      <IconButton
                                        onClick={() => setEditingId(contentType.id)}
                                        sx={{ color: "var(--icon-color)" }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                }
                              >
                                <content.Component
                                  m={metrics}
                                  colors={currentColors}
                                  editedText={
                                    contentType.filter?.find((f) => f.type === EDITED_TEXT_FILTER)?.value ?? ""
                                  }
                                  onEditedTextChange={(v) =>
                                    handleSaveFilter(EDITED_TEXT_FILTER, v, setSelectedReportList, contentType.id)
                                  }
                                  editing={editingId === contentType.id}
                                  onEditEnd={() => setEditingId(null)}
                                />
                              </ReportCard>
                            )}
                          </Box>
                        </Grid>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          mb: 2,
        }}
      >
        {selectedReportList.length === 0 && !isCreatingReport ? (
          <NewReport
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            dateRange={dateRange}
            setDateRange={setDateRange}
            type={type}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: "100%",
                gap: 1,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AssessmentIcon sx={{ fontSize: "40px", color: "var(--icon-color)" }} />
                    {isEditingName ? (
                      <input
                        type="text"
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                        onBlur={() => setIsEditingName(false)}
                        onKeyDown={(e) => e.key === "Enter" && setIsEditingName(false)}
                        autoFocus
                        style={{
                          color: "var(--text-color)",
                          outline: "none",
                          border: "1px solid var(--button-color)",
                          borderRadius: "5px",
                          fontSize: "28px",
                          padding: "3px",
                        }}
                      />
                    ) : (
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "3px",
                          "&:hover": {
                            color: "var(--button-hover-color)",
                          },
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          color: "var(--text-color)",
                        }}
                        onClick={() => {
                          setIsEditingName(true);
                        }}
                      >
                        {reportName}
                      </Typography>
                    )}
                  </Box>

                  <DateRange
                    selectedPeriod={selectedPeriod}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    config={config}
                  />
                </Box>
                <Box sx={{ display: "flex" }}>
                  {hasChanges && (
                    <Button
                      id="save-report-btn"
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        ...ContainedButtonStyles,
                        backgroundColor: "var(--report-save-icon)",
                        "&:hover": {
                          backgroundColor: "var(--report-save-hover)",
                        },
                        color: "var(--text-color)",
                      }}
                    >
                      <SaveIcon /> {t("Save")}
                    </Button>
                  )}
                  <Tooltip title="새 리포트 생성">
                    <IconButton
                      onClick={() => {
                        setOpenCreateModal(true);
                      }}
                      sx={{ color: "var(--icon-color)" }}
                    >
                      <FileOpenIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="리포트 항목 추가">
                    <IconButton id="add-report-content" onClick={handleAddContextMenu} sx={{ color: "var(--icon-color)" }}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton id="print-report-btn" onClick={handlePrintContextMenu} sx={{ color: "var(--icon-color)" }}>
                    <PrintIcon />
                  </IconButton>
                  <IconButton onClick={handleSettingContextMenu} sx={{ color: "var(--icon-color)" }}>
                    <SettingsIcon />
                  </IconButton>
                </Box>
                <ReportPrintList
                  anchorEl={anchorElPrint}
                  open={printOpen}
                  onClose={handleClose}
                  onClick={handleExportPDF}
                />
                <ReportSettingList anchorEl={anchorElSetting} open={settingOpen} onClose={handleClose} />
              </Box>
              <Box ref={contentRef}>
                {isLoading ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="200px">
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>데이터를 찾고 있습니다...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ height: "fit-content" }}>
                    {metrics && (
                      <ValidationInfo
                        m={metrics}
                        validHistories={rawData?.validHistories}
                        onChange={setValidHistoryId}
                      />
                    )}
                    {renderContent()}
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
      {openCreateModal && (
        <Modal open={openCreateModal} onClose={() => setOpenCreateModal(false)}>
          <TemplateModal
            setOpenCreateModal={setOpenCreateModal}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </Modal>
      )}
      <Menu anchorEl={anchorElAdd} open={addOpen} onClose={handleClose}>
        {Object.entries(REPORT_CONTENTS).map(([key, c]) => (
          <MenuItem key={key} onClick={() => handleMenuItemClick(key)}>
            {c.title}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
